import { styled } from '@linaria/react';
import { Skeleton, Stack } from '@mantine/core';
import FloraIcon from '../../../../assets/icons/iconFlora.svg';
import { HorizontalHealthBar } from '../../../../components/horizontal-health-bar/horizontal-health-bar';
import { formatAiSummary } from '../../../../helpers/string-helpers/string-helpers';
import { newCOLORS } from '../../../../styles/colors';
import { skyDark, skyLight, skyTight } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import type { WorkPeriod } from '../../work-periods.type';
import { WorkPeriodType } from '../../work-periods.type';

type HealthScoreSectionProps = {
  score: number;
  selectedWorkPeriod: WorkPeriod | null;
  selectedWorkPeriodType: WorkPeriodType;
};

export function HealthScoreSection({ score, selectedWorkPeriod, selectedWorkPeriodType }: HealthScoreSectionProps) {
  return (
    <Stack>
      <Stack gap={2} mt={16}>
        <Text weight="bold" size="medium">
          Health Score
        </Text>
        <Text size="small" color={skyDark}>
          Overall health of the work period
        </Text>
      </Stack>
      <ScoreContainer>
        <LeftBox>
          <HorizontalHealthBar
            heading="Performance Score"
            tooltip="Overall health score based on key process measures"
            health={score}
            showNumber={false}
          />
        </LeftBox>

        {selectedWorkPeriod?.ai_insights && (
          <AIInsights selectedWorkPeriod={selectedWorkPeriod} selectedWorkPeriodType={selectedWorkPeriodType} />
        )}
      </ScoreContainer>
    </Stack>
  );
}

/**
 * Flora AI Insights panel for a work period assessment.
 *
 * Renders one of four mutually exclusive states based on the work period and
 * its cached `ai_insights`, and appends the fallibility disclaimer to the
 * populated state only — never to an empty/placeholder state.
 *
 * Responsibilities:
 * 1. Ad-hoc (Custom) work periods always render a "Coming Soon" placeholder
 * 2. With no work period selected, prompt the user to select one
 * 3. When `ai_insights` is missing or reduces to no renderable overview/bullets
 *    (e.g. whitespace-only), render "No insights available" rather than a blank
 *    panel beneath the disclaimer
 * 4. Otherwise format the summary into an overview + bullet list and append the
 *    "Flora can make mistakes…" disclaimer
 *
 * @param {object} props - Component props
 * @param {WorkPeriod | null} props.selectedWorkPeriod - Work period whose cached
 *   `ai_insights` populate the panel; null renders the selection prompt
 * @param {WorkPeriodType} props.selectedWorkPeriodType - Drives the Custom →
 *   "Coming Soon" branch
 * @param {boolean} [props.addMarginTop=false] - Adds a 16px top margin when the
 *   panel renders stand-alone (full-width assessment path) rather than beside
 *   the health score
 * @returns {JSX.Element} The Flora AI Insights panel
 *
 * @example
 * <AIInsights selectedWorkPeriod={workPeriod} selectedWorkPeriodType={WorkPeriodType.Defined} />
 */
export function AIInsights({
  selectedWorkPeriod,
  selectedWorkPeriodType,
  addMarginTop = false,
}: {
  selectedWorkPeriod: WorkPeriod | null;
  selectedWorkPeriodType: WorkPeriodType;
  addMarginTop?: boolean;
}) {
  // For Ad-hoc work periods, always show "Coming Soon" regardless of AI data
  if (selectedWorkPeriodType === WorkPeriodType.Custom) {
    return (
      <AIInsightsContainer style={{ marginTop: addMarginTop ? 16 : 0 }}>
        <AIInsightsContent>
          <TitleContainer>
            <img src={FloraIcon} alt="Flora" style={{ width: '20px', height: '20px' }} />
            <Text weight="medium" size="medium" color="#666">
              Flora AI Insights
            </Text>
          </TitleContainer>
          <Text size="small" color="dimmed" style={{ textAlign: 'left' }}>
            Coming Soon
          </Text>
        </AIInsightsContent>
      </AIInsightsContainer>
    );
  }

  if (!selectedWorkPeriod) {
    return (
      <AIInsightsContainer style={{ marginTop: addMarginTop ? 16 : 0 }}>
        <AIInsightsContent>
          <TitleContainer>
            <img src={FloraIcon} alt="Flora" style={{ width: '20px', height: '20px' }} />
            <Text weight="medium" size="medium" color="#666">
              Flora AI Insights
            </Text>
          </TitleContainer>
          <Text size="small" color="dimmed" style={{ textAlign: 'left' }}>
            Select a work period to view AI insights
          </Text>
        </AIInsightsContent>
      </AIInsightsContainer>
    );
  }

  // Parse once: a truthy-but-empty payload (whitespace, or prose that reduces to no
  // overview and no bullets) must fall through to the empty state instead of rendering a
  // blank panel beneath the "Flora can make mistakes" disclaimer.
  const { overview, bullets } = formatAiSummary(selectedWorkPeriod.ai_insights ?? '');
  const hasRenderableInsights = overview.length > 0 || bullets.length > 0;

  if (!hasRenderableInsights) {
    return (
      <AIInsightsContainer style={{ marginTop: addMarginTop ? 16 : 0 }}>
        <AIInsightsContent>
          <TitleContainer>
            <img src={FloraIcon} alt="Flora" style={{ width: '20px', height: '20px' }} />
            <Text weight="medium" size="medium" color="#666">
              Flora AI Insights
            </Text>
          </TitleContainer>
          <Text size="small" color="dimmed" style={{ textAlign: 'left' }}>
            No insights available for {selectedWorkPeriod.name}
          </Text>
        </AIInsightsContent>
      </AIInsightsContainer>
    );
  }

  return (
    <AIInsightsContainer style={{ marginTop: addMarginTop ? 16 : 0 }} data-testid="ai-insights">
      <AIInsightsContent>
        <TitleContainer>
          <img src={FloraIcon} alt="Flora" style={{ width: '20px', height: '20px' }} />
          <Text weight="medium" size="medium" color="#666">
            Flora AI Insights
          </Text>
        </TitleContainer>
        <SummaryContainer>
          {overview && (
            <Text size="small" color="dark" style={{ textAlign: 'left', marginBottom: bullets.length > 0 ? '8px' : 0 }}>
              {overview}
            </Text>
          )}
          {bullets.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: '16px', textAlign: 'left' }}>
              {bullets.map((bullet, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>
                  {bullet}
                </li>
              ))}
            </ul>
          )}
        </SummaryContainer>
        {/*
          Pin the disclaimer to the bottom of the panel. `AIInsightsContainer` is `flex: 1`
          inside a column layout, so on tall viewports (full-width assessment path) or when a
          short health bar drives the row height (side-by-side path) the panel grows taller than
          its content. `marginTop: auto` absorbs that free space ABOVE the footer so the gap
          BELOW it stays the constant 24px `AIInsightsContent` padding on every screen;
          `paddingTop` keeps a minimum separation from the insights when the panel is content-sized.
        */}
        <Text
          size="tiny"
          color="var(--ink-lighter)"
          style={{ textAlign: 'center', lineHeight: '1.4', marginTop: 'auto', paddingTop: 16, flexShrink: 0 }}
        >
          Flora can make mistakes. Responses may not be entirely accurate and forecasts are estimates.
        </Text>
      </AIInsightsContent>
    </AIInsightsContainer>
  );
}

/**
 * Loading-state placeholder for {@link HealthScoreSection}. Renders with the
 * same outer dimensions (header + 200px-tall side-by-side boxes) so the layout
 * does not shift when real data arrives.
 *
 * If the selected work period already has cached AI insights we render the
 * real {@link AIInsights} panel beside the score skeleton — those insights
 * live on `selectedWorkPeriod` and require no fetch. The caller MUST pass the
 * actual `selectedWorkPeriodType`; the AI insights component branches on it
 * (Custom periods always render "Coming Soon"), so a hard-coded default would
 * cause a content flip when the score query resolves.
 *
 * @param {object} props - Component props
 * @param {WorkPeriod | null | undefined} props.selectedWorkPeriod - The
 *   currently selected work period, used to populate the AI insights panel
 *   in parallel with the loading score
 * @param {WorkPeriodType} props.selectedWorkPeriodType - The current work
 *   period type, forwarded to {@link AIInsights} so its branching matches the
 *   real `HealthScoreSection` once data arrives
 * @returns {JSX.Element} The placeholder block
 */
export function HealthScoreSectionSkeleton({
  selectedWorkPeriod,
  selectedWorkPeriodType,
}: {
  selectedWorkPeriod: WorkPeriod | null | undefined;
  selectedWorkPeriodType: WorkPeriodType;
}) {
  return (
    <Stack data-testid="health-score-section-skeleton" role="status" aria-busy="true" aria-label="Loading health score">
      <Stack gap={2} mt={16}>
        <Text weight="bold" size="medium">
          Health Score
        </Text>
        <Text size="small" color={skyDark}>
          Overall health of the work period
        </Text>
      </Stack>
      <ScoreContainer>
        <LeftBox>
          <Skeleton height={32} radius="md" width="80%" />
        </LeftBox>
        {selectedWorkPeriod?.ai_insights ? (
          <AIInsights selectedWorkPeriod={selectedWorkPeriod} selectedWorkPeriodType={selectedWorkPeriodType} />
        ) : (
          <SkeletonInsightsBox>
            <Skeleton height={20} width="40%" radius="sm" />
            <Skeleton height={12} width="100%" radius="sm" mt={12} />
            <Skeleton height={12} width="92%" radius="sm" mt={8} />
            <Skeleton height={12} width="80%" radius="sm" mt={8} />
          </SkeletonInsightsBox>
        )}
      </ScoreContainer>
    </Stack>
  );
}

const ScoreContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const SkeletonInsightsBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${skyTight};
  border-radius: 8px;
  padding: 24px;
  min-height: 200px;
  border: 1px solid ${skyLight};
`;

const LeftBox = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: ${skyTight};
  border-radius: 8px;
  border: 1px solid ${skyLight};
  min-height: 200px;
`;

// Flora gradient for border
const floraGradient = `linear-gradient(135deg, 
  ${newCOLORS.yellow3} 0%, 
  ${newCOLORS.blue2} 25%, 
  ${newCOLORS.pink2} 50%, 
  ${newCOLORS.red3} 75%, 
  ${newCOLORS.purple3} 100%
)`;

const AIInsightsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${floraGradient};
  border-radius: 8px;
  padding: 2px;
  min-height: 200px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const AIInsightsContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 5px;
  padding: 24px;
  height: 100%;
  min-height: 180px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

const SummaryContainer = styled.div`
  text-align: left;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: min(60vh, 480px);
  min-height: 0;
  overflow-wrap: break-word;

  ul {
    list-style-type: disc;
    margin: 0;
    padding-left: 20px;

    li {
      margin-bottom: 12px;
      line-height: 1.5;
      color: #333;
      font-size: 14px;
    }
  }
`;
